import React, { useState, useEffect } from 'react';
import { Move, RotateCcw, Scale } from 'lucide-react';

export default function NumericTransformPanel({ 
  selectedObject, 
  onTransformChange,
  isVisible = true,
  currentTransformValues,
  initialTransform = null,
  transformMode = 'translate',
  onModeChange = null,
  scaleLimits = { min: 0.1, max: 3.0 },
  positionLimits = { x: { min: -5, max: 5 }, y: { min: -3, max: 5 }, z: { min: -5, max: 5 } }
}) {
  
  const [values, setValues] = useState({
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 }
  });

  // Button styles for transform mode buttons
  const buttonStyles = {
    base: 'w-8 h-8 rounded-lg bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#2a2a2a] hover:border-[#3a3a3a] transition-all duration-200 flex items-center justify-center text-white',
    active: 'bg-[#3a3a3a] border-[#4a4a4a] text-white'
  };

  const getButtonStyle = (mode) => {
    if (transformMode === mode) {
      return `${buttonStyles.base} ${buttonStyles.active}`;
    }
    return buttonStyles.base;
  };

  // Update values when selected object changes
  useEffect(() => {
    if (selectedObject) {
      setValues({
        position: {
          x: Math.round(selectedObject.position.x * 1000) / 1000,
          y: Math.round(selectedObject.position.y * 1000) / 1000,
          z: Math.round(selectedObject.position.z * 1000) / 1000
        },
        rotation: {
          x: Math.round(selectedObject.rotation.x * 1000) / 1000,
          y: Math.round(selectedObject.rotation.y * 1000) / 1000,
          z: Math.round(selectedObject.rotation.z * 1000) / 1000
        },
        scale: {
          x: Math.round(selectedObject.scale.x * 1000) / 1000,
          y: Math.round(selectedObject.scale.y * 1000) / 1000,
          z: Math.round(selectedObject.scale.z * 1000) / 1000
        }
      });
    }
  }, [selectedObject]);

  // Update values when currentTransformValues change (from transform controls)
  useEffect(() => {
    if (currentTransformValues) {
      setValues(currentTransformValues);
    }
  }, [currentTransformValues]);

  // Handle input changes
  const handleValueChange = (type, axis, value) => {
    let numValue = parseFloat(value) || 0;
    
    // Apply position limits for position inputs
    if (type === 'position') {
      numValue = Math.max(positionLimits[axis].min, Math.min(positionLimits[axis].max, numValue));
    }
    
    // Apply scale limits for scale inputs
    if (type === 'scale') {
      numValue = Math.max(scaleLimits.min, Math.min(scaleLimits.max, numValue));
    }
    
    // Ensure values object is properly structured before accessing properties
    const safeValues = {
      position: values.position || { x: 0, y: 0, z: 0 },
      rotation: values.rotation || { x: 0, y: 0, z: 0 },
      scale: values.scale || { x: 1, y: 1, z: 1 }
    };
    
    const newValues = {
      ...safeValues,
      [type]: {
        ...safeValues[type],
        [axis]: numValue
      }
    };
    
    setValues(newValues);
    
    // Apply transform to the selected object
    if (selectedObject && onTransformChange) {
      const position = [newValues.position.x, newValues.position.y, newValues.position.z];
      const rotation = [newValues.rotation.x, newValues.rotation.y, newValues.rotation.z];
      const scale = [newValues.scale.x, newValues.scale.y, newValues.scale.z];
      
      // Apply transform directly to the 3D object
      selectedObject.position.set(...position);
      selectedObject.rotation.set(...rotation);
      selectedObject.scale.set(...scale);
      selectedObject.updateMatrixWorld();
      
      // Notify parent about the transform update
      onTransformChange({
        position,
        rotation,
        scale,
        transformUpdated: true
      });
    }
  };

  // Handle input blur - apply final values
  const handleInputBlur = (type, axis, value) => {
    const numValue = parseFloat(value) || 0;
    handleValueChange(type, axis, numValue);
  };

  // Reset to original uploaded values
  const handleReset = (type) => {
    // Use initial transform if available, otherwise fall back to defaults
    let resetValues;
    
    if (initialTransform && initialTransform[type]) {
      // Reset to the original uploaded values
      resetValues = {
        x: initialTransform[type].x || 0,
        y: initialTransform[type].y || 0,
        z: initialTransform[type].z || (type === 'scale' ? 1 : 0)
      };
    } else {
      // Fallback to default values if no initial transform is available
      const defaults = {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 }
      };
      resetValues = defaults[type];
    }
    
    setValues(prev => ({
      ...prev,
      [type]: resetValues
    }));
    
    if (selectedObject && onTransformChange) {
      const newValues = {
        ...values,
        [type]: resetValues
      };
      
      // Apply transform directly to the 3D object
      if (type === 'position') {
        selectedObject.position.set(resetValues.x, resetValues.y, resetValues.z);
      } else if (type === 'rotation') {
        selectedObject.rotation.set(resetValues.x, resetValues.y, resetValues.z);
      } else if (type === 'scale') {
        selectedObject.scale.set(resetValues.x, resetValues.y, resetValues.z);
      }
      selectedObject.updateMatrixWorld();
      
      onTransformChange({
        position: [newValues.position.x, newValues.position.y, newValues.position.z],
        rotation: [newValues.rotation.x, newValues.rotation.y, newValues.rotation.z],
        scale: [newValues.scale.x, newValues.scale.y, newValues.scale.z],
        transformUpdated: true
      });
    }
  };

  if (!isVisible) return null;

  const inputStyles = "w-16 h-8 px-2 text-xs bg-white/10 border border-white/20 rounded text-white text-center focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50";
  const labelStyles = "text-xs text-white/70 font-medium";
  const axisColors = {
    x: "text-red-400",
    y: "text-green-400", 
    z: "text-blue-400"
  };

  // Ensure values object is properly structured before rendering
  const safeValues = {
    position: values.position || { x: 0, y: 0, z: 0 },
    rotation: values.rotation || { x: 0, y: 0, z: 0 },
    scale: values.scale || { x: 1, y: 1, z: 1 }
  };

  return (
    <div className="bg-black/40 backdrop-blur-sm rounded-xl ring-1 ring-white/20 p-3 min-w-[200px]">
      <div className="text-xs text-white/70 text-center mb-3 font-medium">TRANSFORM CONTROLS</div>
      
      {/* Position */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => onModeChange && onModeChange('translate')}
            className={getButtonStyle('translate')}
            title="Move Mode (G)"
          >
            <Move size={14} />
          </button>
          <span className={labelStyles}>Position</span>
          <button
            onClick={() => handleReset('position')}
            className="ml-auto text-xs text-white/50 hover:text-white/70 hover:bg-white/10 px-2 py-1 rounded transition-colors"
            title="Reset Position"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {['x', 'y', 'z'].map((axis) => (
            <div key={axis} className="flex flex-col">
              <label className={`text-xs ${axisColors[axis]} mb-1`}>
                {axis.toUpperCase()}
              </label>
              <input
                type="number"
                step="0.001"
                min={positionLimits[axis].min}
                max={positionLimits[axis].max}
                value={safeValues.position[axis]}
                onChange={(e) => handleValueChange('position', axis, e.target.value)}
                onBlur={(e) => handleInputBlur('position', axis, e.target.value)}
                className={inputStyles}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Rotation */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => onModeChange && onModeChange('rotate')}
            className={getButtonStyle('rotate')}
            title="Rotate Mode (R)"
          >
            <RotateCcw size={14} />
          </button>
          <span className={labelStyles}>Rotation</span>
          <button
            onClick={() => handleReset('rotation')}
            className="ml-auto text-xs text-white/50 hover:text-white/70 hover:bg-white/10 px-2 py-1 rounded transition-colors"
            title="Reset Rotation"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {['x', 'y', 'z'].map((axis) => (
            <div key={axis} className="flex flex-col">
              <label className={`text-xs ${axisColors[axis]} mb-1`}>
                {axis.toUpperCase()}
              </label>
              <input
                type="number"
                step="0.001"
                value={safeValues.rotation[axis]}
                onChange={(e) => handleValueChange('rotation', axis, e.target.value)}
                onBlur={(e) => handleInputBlur('rotation', axis, e.target.value)}
                className={inputStyles}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Scale */}
      <div className="mb-2">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => onModeChange && onModeChange('scale')}
            className={getButtonStyle('scale')}
            title="Scale Mode (S)"
          >
            <Scale size={14} />
          </button>
          <span className={labelStyles}>Scale</span>
          <button
            onClick={() => handleReset('scale')}
            className="ml-auto text-xs text-white/50 hover:text-white/70 hover:bg-white/10 px-2 py-1 rounded transition-colors"
            title="Reset Scale"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {['x', 'y', 'z'].map((axis) => (
            <div key={axis} className="flex flex-col">
              <label className={`text-xs ${axisColors[axis]} mb-1`}>
                {axis.toUpperCase()}
              </label>
              <input
                type="number"
                step="0.001"
                min={scaleLimits.min}
                max={scaleLimits.max}
                value={safeValues.scale[axis]}
                onChange={(e) => handleValueChange('scale', axis, e.target.value)}
                onBlur={(e) => handleInputBlur('scale', axis, e.target.value)}
                className={inputStyles}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}